import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { adminApi } from '../../lib/adminApi';
import {
  RefreshCw, Plus, Edit, Trash2, ToggleRight, Upload, MoveVertical, LogIn, XCircle, Download,
} from 'lucide-react';

const ACTION_META = {
  'create':           { icon: Plus,         color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'update':           { icon: Edit,         color: 'bg-blue-100 text-blue-700 border-blue-200' },
  'delete':           { icon: Trash2,       color: 'bg-rose-100 text-rose-700 border-rose-200' },
  'publish-toggle':   { icon: ToggleRight,  color: 'bg-violet-100 text-violet-700 border-violet-200' },
  'upload':           { icon: Upload,       color: 'bg-amber-100 text-amber-700 border-amber-200' },
  'reorder':          { icon: MoveVertical, color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  'login-success':    { icon: LogIn,        color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'login-failure':    { icon: XCircle,      color: 'bg-red-100 text-red-700 border-red-200' },
};

const COLLECTIONS = ['', 'projects', 'events', 'gallery', 'videos', 'careers', 'about_content', 'about_modal', 'room_booking', 'uploads'];
const ACTIONS = ['', 'create', 'update', 'delete', 'publish-toggle', 'upload', 'reorder', 'login-success', 'login-failure'];

const formatTs = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-MY', { hour12: false });
};

export const AuditLogViewer = ({ compact = false, limit = 100 }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ collection: '', actor: '', action: '' });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const res = await adminApi.get(`/audit-log?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('audit-log', e);
    } finally {
      setLoading(false);
    }
  }, [filters, limit]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const res = await adminApi.get(`/audit-log/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `ewrf-audit-log-${ts}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('csv export', e);
    }
  };

  if (compact) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Recent Activity
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">No activity yet.</p>
          ) : (
            <ul className="space-y-2">
              {items.slice(0, 5).map((it, i) => {
                const meta = ACTION_META[it.action] || { icon: Edit, color: 'bg-gray-100 text-gray-700' };
                const Icon = meta.icon;
                return (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold capitalize">{it.action.replace('-', ' ')}</span>
                        <span className="text-gray-400 truncate">{it.collection}</span>
                      </div>
                      <div className="text-gray-500 truncate">{it.summary || it.object_id}</div>
                      <div className="text-[10px] text-gray-400">{formatTs(it.ts)} • {it.actor}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="audit-log-viewer">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <h2 className="text-2xl font-bold flex-1">Audit Log <span className="text-sm font-normal text-gray-500">({total} events)</span></h2>
        <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
        <Button onClick={load} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Collection</Label>
            <Select value={filters.collection || 'all'} onValueChange={(v) => setFilters({ ...filters, collection: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="filter-collection"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {COLLECTIONS.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="filter-action"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {ACTIONS.filter(Boolean).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Actor</Label>
            <Input value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} placeholder="username" data-testid="filter-actor" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No audit events match these filters.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((it, i) => {
                const meta = ACTION_META[it.action] || { icon: Edit, color: 'bg-gray-100 text-gray-700' };
                const Icon = meta.icon;
                const isExpanded = expanded === i;
                return (
                  <li key={i} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-2 mb-1">
                          <span className="font-bold capitalize">{it.action.replace('-', ' ')}</span>
                          {it.collection && <Badge variant="outline" className="text-[10px]">{it.collection}</Badge>}
                          {!it.success && <Badge className="bg-red-100 text-red-700 text-[10px]">FAILED</Badge>}
                        </div>
                        <div className="text-sm text-gray-700 mb-1 truncate">{it.summary || <span className="text-gray-400 italic">no summary</span>}</div>
                        <div className="text-xs text-gray-500">
                          <span className="font-mono">{it.object_id || '—'}</span> • {formatTs(it.ts)} • by <span className="font-medium">{it.actor}</span>
                        </div>
                        {(it.diff && Object.keys(it.diff).length > 0) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs mt-1"
                            onClick={() => setExpanded(isExpanded ? null : i)}
                          >
                            {isExpanded ? 'Hide diff' : 'Show diff'}
                          </Button>
                        )}
                        {isExpanded && (
                          <pre className="mt-2 text-[10px] bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto max-h-72">
                            {JSON.stringify(it.diff, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogViewer;
